import { useEffect, useState } from 'react'
import { supabase } from '../../../../lib/supabase'



const STATUS_FILTERS = ['all', 'pending', 'paid', 'fulfilled', 'cancelled']

const statusColor = (status) => {
  switch (status) {
    case 'paid': return 'text-green-600 dark:text-green-400'
    case 'fulfilled': return 'text-blue-600 dark:text-blue-400'
    case 'pending': return 'text-yellow-600 dark:text-yellow-400'
    case 'cancelled': return 'text-red-500'
    default: return 'text-gray-400'
  }
}

export default function OrdersTable() {
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all')
  const [expanded, setExpanded] = useState(null)

  const fetchOrders = async () => {
    const { data } = await supabase
      .from('orders')
      .select('*, supporters(full_name, email, membership_id)')
      .order('created_at', { ascending: false })
    if (data) setOrders(data)
    setLoading(false)
  }

  useEffect(() => { fetchOrders() }, [])

  const handleMarkFulfilled = async (order) => {
    await supabase.from('orders').update({ status: 'fulfilled' }).eq('id', order.id)
    fetchOrders()
  }

  const handleExportCSV = () => {
    const rows = [
      ['Order ID', 'Member', 'Email', 'Membership ID', 'Items', 'Total', 'Status', 'Date'],
      ...filtered.map(o => [
        o.id,
        o.supporters?.full_name || '',
        o.supporters?.email || '',
        o.supporters?.membership_id || '',
        (o.items || []).map(i => `${i.name} x${i.quantity}`).join('; '),
        `ZMW ${Number(o.total_amount).toFixed(2)}`,
        o.status,
        new Date(o.created_at).toLocaleDateString(),
      ])
    ]
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `mwfc-orders-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const filtered = filter === 'all' ? orders : orders.filter(o => o.status === filter)

  return (
    <div>
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row justify-between gap-3 mb-4">
        {/* Status filter tabs */}
        <div className="flex gap-0 border border-gray-200 dark:border-white/10 overflow-x-auto">
          {STATUS_FILTERS.map(s => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-4 py-2 text-xs font-bold uppercase tracking-widest whitespace-nowrap transition-colors ${
                filter === s
                  ? 'bg-green-600 text-white'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
        <button
          onClick={handleExportCSV}
          disabled={filtered.length === 0}
          className="bg-gray-900 dark:bg-white/10 hover:bg-gray-800 dark:hover:bg-white/20 disabled:opacity-40 text-white px-5 py-2 text-xs font-bold uppercase tracking-widest transition-colors whitespace-nowrap"
        >
          Export CSV
        </button>
      </div>

      {loading ? (
        <div className="text-gray-400 text-sm py-10 text-center">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="text-gray-400 text-sm py-10 text-center border border-dashed border-gray-200 dark:border-white/10">
          No orders found.
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {filtered.map(order => (
            <div key={order.id} className="border border-gray-200 dark:border-white/10 bg-gray-100 dark:bg-white/5">
              {/* Row */}
              <div className="flex items-center gap-4 px-4 py-3">
                <div className="flex-1 min-w-0">
                  <div className="font-black text-sm text-gray-900 dark:text-white uppercase tracking-tight truncate">
                    {order.supporters?.full_name || 'Unknown'}
                  </div>
                  <div className="text-xs text-gray-500 flex flex-wrap gap-2 mt-0.5">
                    <span>{order.supporters?.membership_id || ''}</span>
                    <span>·</span>
                    <span>ZMW {Number(order.total_amount).toFixed(2)}</span>
                    <span>·</span>
                    <span>{new Date(order.created_at).toLocaleDateString()}</span>
                  </div>
                </div>

                <div className={`text-xs font-bold uppercase tracking-widest shrink-0 ${statusColor(order.status)}`}>
                  {order.status}
                </div>

                <div className="flex items-center gap-3 shrink-0">
                  <button
                    onClick={() => setExpanded(expanded === order.id ? null : order.id)}
                    className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white font-bold uppercase tracking-widest transition-colors"
                  >
                    {expanded === order.id ? 'Hide' : 'View'}
                  </button>
                  {order.status === 'paid' && (
                    <>
                      <span className="text-gray-300 dark:text-white/20">|</span>
                      <button
                        onClick={() => handleMarkFulfilled(order)}
                        className="text-xs text-blue-500 hover:text-blue-400 font-bold uppercase tracking-widest transition-colors"
                      >
                        Mark Fulfilled
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Expanded order items */}
              {expanded === order.id && (
                <div className="px-4 pb-4 border-t border-gray-200 dark:border-white/10 pt-3">
                  <div className="text-xs text-gray-500 uppercase tracking-widest font-semibold mb-2">Order Items</div>
                  <div className="flex flex-col gap-1">
                    {(order.items || []).map((item, i) => (
                      <div key={i} className="flex justify-between text-sm text-gray-700 dark:text-gray-300">
                        <span>{item.name} × {item.quantity}</span>
                        <span>ZMW {(item.price * item.quantity).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between text-sm font-black text-gray-900 dark:text-white mt-2 pt-2 border-t border-gray-200 dark:border-white/10">
                    <span>Total</span>
                    <span>ZMW {Number(order.total_amount).toFixed(2)}</span>
                  </div>
                  {order.supporters?.email && (
                    <div className="text-xs text-gray-400 mt-2">{order.supporters.email}</div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}